import classNames from "classnames";
import React, { useCallback, useContext, useEffect, useRef, useState } from "react";
import SVGIcon from "web/react/components/svg-icon/svg-icon";
import { useMutationObserver } from "web/react/hooks/use-mutation-observer/use-mutation-observer";
import globals from "web/script/modules/globals";
import styles from "./accordion.css";

export type AccordionHook = {
    accordionName: string | undefined;
    nonInteraction: boolean;
};

interface AccordionContextProps {
    expanded: boolean;
    onToggle: (any) => void;
}

export const AccordionContext = React.createContext<AccordionContextProps>({
    expanded: false,
    onToggle: () => {},
});

interface AccordionTitleProps {
    children: React.ReactNode;
    className?: string;
    activeClassName?: string;
}

function AccordionTitle({
    children,
    className,
    activeClassName = "",
}: AccordionTitleProps): React.ReactElement {
    const { onToggle, expanded } = useContext(AccordionContext);

    return (
        <div
            className={classNames(styles.title, className, { [activeClassName]: expanded })}
            onClick={onToggle}
        >
            {children}
            <SVGIcon
                name="chevron-thin"
                className={classNames(styles.chevronIcon, {
                    [styles.chevronIconActive]: expanded,
                })}
            />
        </div>
    );
}

interface AccordionContentProps {
    children: React.ReactNode;
    className?: string;
}

function AccordionContent({ children, className }: AccordionContentProps): React.ReactElement {
    const { expanded } = useContext(AccordionContext);
    const [maxHeight, setMaxHeight] = useState<number | string>(0);
    const contentRef = useRef<HTMLDivElement>(null);

    const adjustMaxHeight = useCallback(() => {
        if (contentRef.current) {
            setMaxHeight(contentRef.current.scrollHeight);
        }
    }, [contentRef, setMaxHeight]);

    // We specifically want to observe text changes of children
    const observerOptions = {
        childList: true,
        subtree: true,
        characterData: true,
    };
    useMutationObserver({ callback: adjustMaxHeight, ref: contentRef, observerOptions });

    useEffect(() => {
        adjustMaxHeight();
    }, [contentRef, children]);

    useEffect(() => {
        if (expanded) {
            globals.window.addEventListener("resize", adjustMaxHeight);
        } else {
            globals.window.removeEventListener("resize", adjustMaxHeight);
        }

        return () => globals.window.removeEventListener("resize", adjustMaxHeight);
    }, [expanded]);

    return (
        <div
            ref={contentRef}
            className={classNames(
                styles.content,
                { [styles.contentExpanded]: expanded },
                className
            )}
            style={{
                maxHeight: expanded ? maxHeight : 0,
            }}
        >
            {children}
        </div>
    );
}

interface ControlledAccordionProps extends ReactComponentProps {
    expanded: boolean;
    onToggle: () => void;
}

function ControlledAccordion({
    children,
    expanded,
    onToggle,
    className,
}: ControlledAccordionProps): React.ReactElement {
    return (
        <AccordionContext.Provider value={{ expanded, onToggle }}>
            <div className={classNames(styles.accordion, className)}>{children}</div>
        </AccordionContext.Provider>
    );
}

interface AccordionProps extends ReactComponentProps {
    initialExpanded?: boolean;
    name?: string;
    onAccordionOpened?: (param: AccordionHook) => void;
    onAccordionClosed?: (param: AccordionHook) => void;
}

function Accordion({
    initialExpanded,
    children,
    name: accordionName,
    onAccordionOpened,
    onAccordionClosed,
    className,
}: AccordionProps): React.ReactElement {
    const [expanded, setExpanded] = useState<boolean>(initialExpanded || false);

    useEffect(() => {
        const nonInteraction = true;
        if (initialExpanded && onAccordionOpened) {
            onAccordionOpened({ accordionName, nonInteraction });
        } else if (!initialExpanded && onAccordionClosed) {
            onAccordionClosed({ accordionName, nonInteraction });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function onToggle(): void {
        const nonInteraction = false;
        if (expanded && onAccordionClosed) {
            onAccordionClosed({ accordionName, nonInteraction });
        } else if (!expanded && onAccordionOpened) {
            onAccordionOpened({ accordionName, nonInteraction });
        }

        setExpanded(!expanded);
    }

    return (
        <ControlledAccordion className={className} expanded={expanded} onToggle={onToggle}>
            {children}
        </ControlledAccordion>
    );
}

Accordion.Controlled = ControlledAccordion;
Accordion.Content = AccordionContent;
Accordion.Title = AccordionTitle;

export default Accordion;
